import './About.css';
import { useEffect, useRef, useState } from 'react';

function About() {
    const [counters, setCounters] = useState({
        tools: 0,
        categories: 0,
        free: 0,
        updated: 0
    });
    
    const [hasAnimated, setHasAnimated] = useState(false);
    const statsRef = useRef(null);
    const featuresRef = useRef(null);
    const missionRef = useRef(null);
    const ctaRef = useRef(null);

    // Number counter animation
    useEffect(() => {
        if (!hasAnimated && statsRef.current) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !hasAnimated) {
                            setHasAnimated(true);
                            
                            // Animate numbers
                            const targets = {
                                tools: 16,
                                categories: 13,
                                free: 40,
                                updated: 24
                            };
                            
                            const duration = 2000; // 2 seconds
                            const stepTime = 20; // update every 20ms
                            const steps = duration / stepTime;
                            
                            let currentStep = 0;
                            const interval = setInterval(() => {
                                currentStep++;
                                const progress = currentStep / steps;
                                
                                setCounters({
                                    tools: Math.min(Math.floor(targets.tools * progress), targets.tools),
                                    categories: Math.min(Math.floor(targets.categories * progress), targets.categories),
                                    free: Math.min(Math.floor(targets.free * progress), targets.free),
                                    updated: Math.min(Math.floor(targets.updated * progress), targets.updated)
                                });
                                
                                if (currentStep >= steps) {
                                    clearInterval(interval);
                                    setCounters({
                                        tools: targets.tools,
                                        categories: targets.categories,
                                        free: targets.free,
                                        updated: targets.updated
                                    });
                                }
                            }, stepTime);
                            
                            observer.disconnect();
                        }
                    });
                },
                { threshold: 0.3 }
            );
            
            observer.observe(statsRef.current);
            return () => observer.disconnect();
        }
    }, [hasAnimated]);

    // Scroll reveal animations for sections
    useEffect(() => {
        const sections = [missionRef, featuresRef, ctaRef];
        const observers = [];
        
        sections.forEach((sectionRef) => {
            if (sectionRef.current) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                entry.target.classList.add('section-visible');
                                observer.unobserve(entry.target);
                            }
                        });
                    },
                    { threshold: 0.2 }
                );
                
                observer.observe(sectionRef.current);
                observers.push(observer);
            }
        });
        
        return () => {
            observers.forEach(observer => observer.disconnect());
        };
    }, []);

    const scrollToFeatures = () => {
        const featuresSection = document.getElementById('why-choose-us');
        if (featuresSection) {
            featuresSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="ai-about" style={{
            maxWidth: '1440px',
            margin: '0 auto',
            fontFamily: "'Inter', sans-serif",
            color: '#1A1A1A',
            backgroundColor: '#FFFFFF',
            overflowX: 'hidden'
        }}>
            {/* Hero Section - Already visible */}
            <section style={{
                padding: '80px 20px',
                background: 'linear-gradient(180deg, #F0F5FF 0%, #FFFFFF 100%)',
                textAlign: 'center'
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto'
                }}>
                    <h1 style={{
                        fontSize: 'clamp(36px, 5vw, 72px)',
                        fontWeight: '700',
                        lineHeight: '1.1',
                        marginBottom: '24px',
                        letterSpacing: '-0.03em'
                    }}>
                        The <span style={{ color: '#2563EB' }}>AI Tools</span> Aggregator
                    </h1>
                    <p style={{
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        lineHeight: '1.6',
                        color: '#666666',
                        marginBottom: '48px'
                    }}>
                        Your ultimate guide to finding the best free, freemium, and discounted AI tools across all categories.
                    </p>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}>
                        <button style={{
                            padding: '12px 24px',
                            backgroundColor: '#2563EB',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }}
                        className='explore'
                            onClick={() => window.location.href = '/category'}
                        >
                            Explore Tools
                        </button>
                        <button style={{
                            padding: '12px 24px',
                            backgroundColor: 'transparent',
                            color: '#1A1A1A',
                            border: '1px solid #1A1A1A',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }}
                        className='works'
                        onClick={scrollToFeatures}
                        >
                            How It Works
                        </button>
                    </div>
                </div>
            </section>

            {/* About Section with scroll reveal */}
            <section ref={missionRef} className="mission-section reveal-section">
                <div className="mission-container">

                    {/* Text Content */}
                    <div className="mission-text">
                        <h2>
                            About <span className="highlight">Our Mission</span>
                        </h2>
                        <p>
                            In the rapidly evolving AI landscape, finding the right tools can be overwhelming. We created this platform to be the "Trivago of AI Tools" - a single destination where you can discover, compare, and access the best AI solutions for your needs.
                        </p>
                        <p>
                            Our team verifies every listing to ensure you get accurate, up-to-date information about pricing, features, and free alternatives.
                        </p>
                    </div>

                    {/* Image */}
                    <div className="mission-image">
                        <img
                            src="https://images.unsplash.com/photo-1639762681057-408e52192e55?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                            alt="AI Tools Dashboard"
                        />
                    </div>
                </div>
            </section>

            {/* Stats Section with counters */}
            <section ref={statsRef} style={{
                padding: '60px 20px',
                backgroundColor: '#1A1A1A',
                color: 'white'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    gap: '30px'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{
                            fontSize: 'clamp(36px, 5vw, 60px)',
                            fontWeight: '700',
                            color: '#3B82F6',
                            marginBottom: '16px'
                        }}>{counters.tools}k+</h3>
                        <p style={{
                            fontSize: 'clamp(14px, 2vw, 18px)',
                            color: '#CCCCCC'
                        }}>AI Tools Listed</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{
                            fontSize: 'clamp(36px, 5vw, 60px)',
                            fontWeight: '700',
                            color: '#3B82F6',
                            marginBottom: '16px'
                        }}>{counters.categories}+</h3>
                        <p style={{
                            fontSize: 'clamp(14px, 2vw, 18px)',
                            color: '#CCCCCC'
                        }}>Categories</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{
                            fontSize: 'clamp(36px, 5vw, 60px)',
                            fontWeight: '700',
                            color: '#3B82F6',
                            marginBottom: '16px'
                        }}>{counters.free}%</h3>
                        <p style={{
                            fontSize: 'clamp(14px, 2vw, 18px)',
                            color: '#CCCCCC'
                        }}>Free/Freemium</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{
                            fontSize: 'clamp(36px, 5vw, 60px)',
                            fontWeight: '700',
                            color: '#3B82F6',
                            marginBottom: '16px'
                        }}>{counters.updated}/7</h3>
                        <p style={{
                            fontSize: 'clamp(14px, 2vw, 18px)',
                            color: '#CCCCCC'
                        }}>Updated</p>
                    </div>
                </div>
            </section>

            {/* Features Section with scroll reveal */}
            <section id="why-choose-us" ref={featuresRef} className="reveal-section" style={{
                padding: '60px 20px',
                textAlign: 'center',
                backgroundColor: '#F8FAFC'
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto 40px'
                }}>
                    <h2 style={{
                        fontSize: 'clamp(32px, 4vw, 48px)',
                        fontWeight: '700',
                        lineHeight: '1.2',
                        marginBottom: '24px',
                        letterSpacing: '-0.02em'
                    }}>
                        Why <span style={{ color: '#2563EB' }}>Choose Us</span>
                    </h2>
                    <p style={{
                        fontSize: 'clamp(16px, 2vw, 18px)',
                        lineHeight: '1.6',
                        color: '#666666'
                    }}>
                        We go beyond just listing tools - we help you make informed decisions.
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    gap: '30px'
                }}>
                    {[
                        {
                            title: 'Verified Information',
                            description: 'Every tool is manually verified for accuracy',
                            icon: '✅'
                        },
                        {
                            title: 'Free Alternatives',
                            description: 'We highlight free options for paid tools',
                            icon: '💡'
                        },
                        {
                            title: 'Use Case Examples',
                            description: 'Practical prompts and implementation guides',
                            icon: '🎯'
                        },
                    ].map((feature, index) => (
                        <div key={index} style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            padding: '30px 20px',
                            boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
                            transition: 'transform 0.3s ease, opacity 0.5s ease',
                            transitionDelay: `${index * 0.1}s`
                        }}>
                            <div style={{
                                fontSize: '40px',
                                marginBottom: '20px'
                            }}>
                                {feature.icon}
                            </div>
                            <h3 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                margin: '0 0 16px'
                            }}>{feature.title}</h3>
                            <p style={{
                                fontSize: '16px',
                                color: '#666666',
                                lineHeight: '1.6'
                            }}>{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section with scroll reveal */}
            <section ref={ctaRef} className="reveal-section" style={{
                padding: '80px 20px',
                background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                color: 'white',
                textAlign: 'center',
                borderRadius: '0'
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto'
                }}>
                    <h2 style={{
                        fontSize: 'clamp(32px, 4vw, 48px)',
                        fontWeight: '700',
                        lineHeight: '1.2',
                        marginBottom: '24px',
                        letterSpacing: '-0.02em'
                    }}>
                        Ready to Find <span style={{ color: '#A5B4FC' }}>Your Perfect AI Tool?</span>
                    </h2>
                    <p style={{
                        fontSize: 'clamp(16px, 2vw, 18px)',
                        lineHeight: '1.6',
                        color: '#E5E7EB',
                        marginBottom: '48px'
                    }}>
                        Join thousands of users who save time and money with our curated AI tools collection.
                    </p>
                    <button style={{
                        padding: '12px 24px',
                        backgroundColor: 'white',
                        color: '#2563EB',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                    }}
                        onClick={() => window.location.href = '/category'}
                    >
                        Browse All Tools
                    </button>
                </div>
            </section>
        </div>
    )
}

export default About;